import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CameraIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { trainingLocationsAPI } from '../../services/trainingLocations'

/**
 * VH-131 / VH-126 Stage B: add a machine by photographing it.
 *
 * VH-126 always intended this to be the way equipment gets added -- "new
 * equipment is confirmed in one tap: name and photo shown, accept or correct".
 * The capability shipped with Stage B, but VH-131's page merge put a prominent
 * "Add equipment" button next to it that opened a BLANK form, so the manual
 * path looked like the main one. Reto clicked it and reasonably reported the
 * feature as broken. This puts the photo first and keeps manual entry as the
 * fallback it was meant to be -- dumbbells and a yoga mat still need it.
 *
 * Three outcomes, all of which have to be handled or the flow lies to you:
 *
 *   recognised, new        -> hand the candidate up; the form opens pre-filled
 *   recognised, already here -> do NOT create a second row. VH-126 is explicit
 *                            that a machine already at this location is
 *                            recognised, not re-created
 *   not recognised         -> say so and offer the blank form, with nothing
 *                            written
 *
 * Abandoning discards the scan. VH-126 requires that a half-finished capture
 * leaves no residue, and quick-scan persists a scan row the moment it runs.
 */
export default function AddEquipmentByPhoto({
  locationId,
  onCandidate,
  onAlreadyHere,
  onManual,
  onCancel,
}) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notRecognised, setNotRecognised] = useState(false)

  const pickPhoto = () => {
    setError(null)
    setNotRecognised(false)
    inputRef.current?.click()
  }

  const onFile = async (event) => {
    const file = event.target.files?.[0]
    // Reset first so re-picking the same file after a failure still fires.
    event.target.value = ''
    if (!file) return

    setBusy(true)
    setError(null)
    setNotRecognised(false)
    try {
      const { data } = await trainingLocationsAPI.quickScan(locationId, file)

      if (data.matched_equipment_id) {
        // Already here. Creating a second row is exactly what VH-126 forbids,
        // and it is also what breaks scan adoption later, since matching is by
        // name and takes the first hit.
        await trainingLocationsAPI.discardScan(data.scan_id).catch(() => {})
        onAlreadyHere?.(data.matched_equipment_id)
        return
      }

      if (!data.recognized || !data.candidate) {
        // An empty state, not an error: the machine may simply be unusual.
        await trainingLocationsAPI.discardScan(data.scan_id).catch(() => {})
        setNotRecognised(true)
        return
      }

      onCandidate?.({ scanId: data.scan_id, candidate: data.candidate })
    } catch (err) {
      setError(
        err?.response?.data?.detail || t('equipment.addByPhoto.failed')
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4" data-testid="add-equipment-by-photo">
      <p className="text-sm text-gray-600">{t('equipment.addByPhoto.intro')}</p>

      <button
        type="button"
        onClick={pickPhoto}
        disabled={busy}
        className="btn-primary w-full flex items-center justify-center disabled:opacity-50"
        data-testid="add-by-photo-button"
      >
        <CameraIcon className="h-5 w-5 mr-2" />
        {busy ? t('equipment.addByPhoto.identifying') : t('equipment.addByPhoto.takePhoto')}
      </button>

      {notRecognised && (
        <p
          className="text-sm text-amber-700"
          role="status"
          data-testid="add-by-photo-unrecognised"
        >
          {t('equipment.addByPhoto.notRecognised')}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600" role="alert" data-testid="add-by-photo-error">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onManual}
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          data-testid="add-manually-link"
        >
          <PencilSquareIcon className="h-4 w-4 mr-1" />
          {t('equipment.addByPhoto.enterManually')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-600 hover:text-gray-800"
          data-testid="add-by-photo-cancel"
        >
          {t('common.cancel')}
        </button>
      </div>

      {/* VH-157: `capture="environment"` — the button says "Take Photo" and
          Reto reported it never opening the camera. Without the attribute the
          phone offers a chooser, and on iOS that lands in the photo library
          rather than the camera, which is not what the label promises.
          
          This deliberately narrows the control: picking an existing image from
          the library is no longer possible HERE. That was the original intent
          ("camera and library both"), but a control labelled Take Photo that
          does not take a photo is the worse of the two, and Reto chose
          camera-direct. If library access is wanted back it needs a second
          affordance, not the removal of this one. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif"
        capture="environment"
        onChange={onFile}
        className="hidden"
        data-testid="add-by-photo-input"
      />
    </div>
  )
}
